import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { WhatsappAssignmentApiService } from '../services/whatsapp-assignment-api.service';
import { WhatsappMessageApiService } from '../services/whatsapp-message-api.service';
import {
  WhatsappComposeOptions,
  WhatsappComposeService,
} from '../services/whatsapp-compose.service';
import {
  WhatsappPhoneResponse,
  WhatsappTemplate,
  isTemplateSendable,
  requiresMediaHeader,
} from '../models/whatsapp.model';
import { WHATSAPP_TEMPLATE_SUGGESTIONS } from '../models/whatsapp-template-suggestions';

/**
 * Ventana flotante para escribirle por WhatsApp a un contacto, gemela del compose
 * de correo y abierta desde el mismo menú de acciones rápidas.
 *
 * WhatsApp no deja mandar texto libre a quien no te escribió en las últimas 24 h,
 * así que aquí sólo se eligen plantillas aprobadas. Todo lo que el usuario tendría
 * que saber de eso (qué es una plantilla, por qué no puede escribir lo que quiera)
 * se resuelve eligiendo de una lista y rellenando huecos con nombre propio.
 */
/** El teléfono reducido a dígitos: es lo único comparable entre formatos de guardado. */
const soloDigitos = (valor: string): string => valor.replace(/\D/g, '');

@Component({
  selector: 'app-whatsapp-compose',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './whatsapp-compose.component.html',
})
export class WhatsappComposeComponent implements OnInit, OnDestroy {
  private readonly compose = inject(WhatsappComposeService);
  private readonly assignmentApi = inject(WhatsappAssignmentApiService);
  private readonly messageApi = inject(WhatsappMessageApiService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);

  private readonly subs = new Subscription();

  isOpen = false;
  minimized = false;
  options: WhatsappComposeOptions = {};

  loading = false;
  sending = false;
  /** Por qué no se puede enviar: conexión caída, sin número asignado, etc. */
  blockedMessage: string | null = null;

  phones: WhatsappPhoneResponse[] = [];
  phoneId: number | null = null;

  templates: WhatsappTemplate[] = [];
  selectedName = '';
  /** Un valor por cada hueco de la plantilla elegida. */
  params: string[] = [];

  to = '';

  ngOnInit(): void {
    this.subs.add(
      this.compose.state$.subscribe((state) => {
        const wasOpen = this.isOpen;
        this.isOpen = state.isOpen;
        this.options = state.options;
        if (state.isOpen && !wasOpen) this.onOpened();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private onOpened(): void {
    this.minimized = false;
    this.blockedMessage = null;
    this.selectedName = '';
    this.params = [];
    this.to = this.options.phone ?? '';
    this.load();
  }

  close(): void {
    this.compose.close();
  }

  toggleMinimize(): void {
    this.minimized = !this.minimized;
  }

  // ═══ Carga ════════════════════════════════════════════════════════════════

  private load(): void {
    this.loading = true;
    this.phones = [];
    this.templates = [];

    this.assignmentApi.myPhones().subscribe({
      next: (phones) => {
        this.phones = phones;
        this.phoneId = phones.length ? phones[0].id : null;
        if (!phones.length) {
          this.loading = false;
          this.blockedMessage =
            'No tienes ningún número de WhatsApp asignado. Pídele a un administrador que te asigne uno en Configuración de WhatsApp.';
          return;
        }
        this.loadTemplates();
      },
      error: () => {
        this.loading = false;
        this.blockedMessage =
          'No se pudo comprobar tu número de WhatsApp. Revisa la conexión en Configuración de WhatsApp.';
      },
    });
  }

  private loadTemplates(): void {
    this.messageApi.listTemplates().subscribe({
      next: (list) => {
        this.loading = false;
        // Sólo las aprobadas son enviables; las demás sólo confundirían aquí.
        this.templates = list.filter(isTemplateSendable);
        if (!this.templates.length) {
          this.blockedMessage =
            'Todavía no hay ningún mensaje aprobado por WhatsApp, y sin uno no se puede iniciar la conversación.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.blockedMessage =
          err?.error?.message ??
          'No se pudieron cargar los mensajes disponibles. Revisa la conexión con WhatsApp.';
      },
    });
  }

  // ═══ Plantilla elegida ════════════════════════════════════════════════════

  get selected(): WhatsappTemplate | null {
    return this.templates.find((t) => t.name === this.selectedName) ?? null;
  }

  /** `recordatorio_reunion` → «Recordatorio reunion». */
  prettyName(name: string | null | undefined): string {
    if (!name) return '';
    const words = name.replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  /**
   * Qué significa cada hueco. Meta no lo devuelve — sólo el texto con {{1}} —,
   * pero de nuestras plantillas sugeridas sí lo sabemos, así que ahí el usuario
   * ve «Nombre del contacto» en vez de «Dato 1».
   */
  paramLabel(index: number): string {
    const known = WHATSAPP_TEMPLATE_SUGGESTIONS.find(
      (s) => s.template.name === this.selectedName
    );
    return known?.paramLabels[index] ?? `Dato ${index + 1}`;
  }

  onTemplateChange(): void {
    const t = this.selected;
    this.params = t ? Array.from({ length: t.paramCount }, (_, i) => this.prefillFor(i)) : [];
  }

  /**
   * Lo que el CRM ya sabe se rellena solo: el nombre del contacto, el de la
   * empresa y el del asesor conectado. Es la diferencia entre «rellena 3 campos»
   * y «revisa y envía».
   */
  private prefillFor(index: number): string {
    const label = this.paramLabel(index).toLowerCase();
    if (label.includes('contacto')) return this.contactFirstName;
    if (label.includes('empresa')) return this.auth.getCurrentBusiness()?.name ?? '';
    if (label.includes('asesor')) return this.auth.getCurrentUser()?.fullname ?? '';
    return '';
  }

  private get contactFirstName(): string {
    return (this.options.fullName ?? '').trim().split(/\s+/)[0] ?? '';
  }

  /** Cabecera de imagen/vídeo/documento: aquí no se puede adjuntar, así que se avisa. */
  get needsMedia(): boolean {
    return requiresMediaHeader(this.selected?.headerFormat);
  }

  get preview(): string {
    const t = this.selected;
    if (!t?.bodyText) return '';
    return t.bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n) => {
      const value = (this.params[Number(n) - 1] || '').trim();
      return value || this.paramLabel(Number(n) - 1).toLowerCase();
    });
  }

  /**
   * La ficha desde la que se abrió la ventana, y sólo si el número sigue siendo el suyo.
   *
   * <p>El destinatario viene precargado pero el campo es editable: si el asesor lo cambia está
   * escribiendo a otra persona, y mandar el id de origen colgaría esa conversación de la ficha
   * equivocada. Se comparan sólo los dígitos porque el CRM guarda el teléfono con espacio
   * («+51 987654321») y lo tecleado puede no llevarlo.</p>
   *
   * <p>Cuando no se manda, el backend resuelve el contacto por teléfono igual que siempre; el id
   * sólo le ahorra tener que adivinarlo.</p>
   */
  private get contactIdForRecipient(): number | undefined {
    const id = this.options.contactId;
    if (!id || !this.options.phone) return undefined;
    return soloDigitos(this.options.phone) === soloDigitos(this.to) ? id : undefined;
  }

  get canSend(): boolean {
    return (
      !!this.phoneId &&
      this.to.replace(/\D/g, '').length >= 8 &&
      !!this.selected &&
      !this.needsMedia &&
      this.params.every((p) => p.trim().length > 0) &&
      !this.sending
    );
  }

  send(): void {
    if (!this.canSend) return;
    const t = this.selected!;
    this.sending = true;

    this.messageApi
      .startConversation({
        whatsappPhoneId: Number(this.phoneId),
        to: this.to.trim(),
        templateName: t.name,
        language: t.language,
        params: this.params.map((p) => p.trim()),
        contactId: this.contactIdForRecipient,
      })
      .subscribe({
        next: () => {
          this.sending = false;
          this.toast.success({
            title: 'Mensaje enviado',
            message: `Se envió por WhatsApp a ${this.options.fullName || this.to}.`,
          });
          this.compose.notifySent();
          this.close();
        },
        error: (err) => {
          this.sending = false;
          this.toast.error({
            title: 'No se pudo enviar',
            message: err?.error?.message ?? 'WhatsApp rechazó el envío.',
          });
        },
      });
  }
}
